import React, { Component, ErrorInfo, ReactNode } from "react";
import { Sparkles, RefreshCw, Home, Activity } from "lucide-react";

interface Props {
    children?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

export class GlobalErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Critical Handshake Interrupted:", error, errorInfo);

        // Native Error Ingestion
        try {
            fetch('/api/ops/capture-error', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    // Add X-CSRF-TOKEN if needed, usually handled by axios/laravel-echo but fetch needs it manually if not using a wrapper
                    // For now assuming api route is stateless or csrf token is in meta
                },
                body: JSON.stringify({
                    message: error.message,
                    stack: error.stack,
                    componentStack: errorInfo.componentStack,
                    url: window.location.href,
                })
            }).catch(e => console.error("Failed to report error natively:", e));
        } catch (e) {
            console.error("Failed to report error natively:", e);
        }
    }

    private handleSync = () => {
        window.location.reload();
    };

    private handleReturnBase = () => {
        window.location.href = "/";
    };

    public render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-[#020617] text-slate-100 p-6 selection:bg-cyan-500/30">
                    <div className="max-w-xl w-full relative group">
                        {/* Radiant Background Glow */}
                        <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 via-violet-500 to-fuchsia-500 rounded-3xl blur-2xl opacity-20 animate-pulse" />

                        <div className="relative bg-slate-900/80 backdrop-blur-3xl border border-white/10 rounded-3xl p-10 text-center shadow-2xl">
                            {/* Animated Icon Container */}
                            <div className="mb-8 relative inline-block">
                                <div className="absolute inset-0 bg-cyan-500/20 rounded-full blur-xl animate-ping" />
                                <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center shadow-lg transform rotate-3 hover:rotate-0 transition-transform duration-500">
                                    <Activity className="w-10 h-10 text-white" />
                                </div>
                            </div>

                            <h1 className="text-3xl font-black tracking-tight mb-4 uppercase">
                                Ecosystem <span className="text-cyan-400">Calibration</span>
                            </h1>

                            <div className="space-y-4 mb-10">
                                <p className="text-slate-400 text-lg font-medium leading-relaxed">
                                    Our AI is currently refining the intelligence stream.
                                    <span className="block italic mt-1 text-slate-500 font-normal">Environmental stability is being restored.</span>
                                </p>

                                <div className="pt-4 flex items-center justify-center gap-2 text-xs font-mono text-cyan-500/60 uppercase tracking-widest">
                                    <RefreshCw className="w-3 h-3 animate-spin" />
                                    Synchronizing Neural Paths...
                                </div>
                            </div>

                            {/* Signal ID (Obfuscated Technical Error) */}
                            <div className="mb-8 p-3 bg-black/40 rounded-xl border border-white/5 font-mono text-[10px] text-slate-500 uppercase">
                                Signal ID: INTEL-{Math.random().toString(36).substring(7).toUpperCase()}-BETA
                            </div>

                            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                                <button
                                    onClick={this.handleSync}
                                    className="w-full sm:w-auto px-8 py-4 bg-white text-slate-900 rounded-2xl font-black text-sm tracking-widest uppercase hover:bg-cyan-400 transition-all active:scale-95 shadow-lg shadow-white/5"
                                >
                                    Synchronize Now
                                </button>

                                <button
                                    onClick={this.handleReturnBase}
                                    className="w-full sm:w-auto px-8 py-4 bg-slate-800 text-slate-300 rounded-2xl font-bold text-sm tracking-widest uppercase hover:bg-slate-700 transition-all border border-white/5"
                                >
                                    Return to Base
                                </button>
                            </div>
                        </div>

                        {/* Subtle Background Elements */}
                        <div className="absolute -top-20 -left-20 w-40 h-40 bg-cyan-500/10 rounded-full blur-[80px]" />
                        <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-violet-500/10 rounded-full blur-[80px]" />
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
